import { PDFDocument, rgb, StandardFonts, PDFName, PDFString } from 'pdf-lib';
import type { PdfGenerationResult, BunnyCredentialsResponse, ProductCategory } from '@/types';
import { logger } from '@/lib/logger';
import { PDF_LINK_TEXT, generateSlug } from '@/constants/patterns';

export class PdfService {
  async generateDeliveryPdf(
    driveLink: string,
    productTitle: string,
    templateUrl: string,
    uploadToBunny: (blob: Blob, fileName: string, contentType: string, credentials: BunnyCredentialsResponse) => Promise<string>,
    credentials: BunnyCredentialsResponse
  ): Promise<PdfGenerationResult> {
    const templateBytes = await fetch(templateUrl).then(res => res.arrayBuffer());
    const pdfDoc = await PDFDocument.load(templateBytes);
    const pages = pdfDoc.getPages();
    const firstPage = pages[0];

    const { width, height } = firstPage.getSize();
    const fontSize = 16;
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const textWidth = font.widthOfTextAtSize(PDF_LINK_TEXT, fontSize);
    const textHeight = fontSize;

    const linkX = width / 2 - textWidth / 2;
    const linkY = height / 2;

    firstPage.drawText(PDF_LINK_TEXT, {
      x: linkX,
      y: linkY,
      size: fontSize,
      font,
      color: rgb(0, 0.53, 0.71),
    });

    const linkAnnotation = pdfDoc.context.obj({
      Type: 'Annot',
      Subtype: 'Link',
      Rect: [linkX, linkY, linkX + textWidth, linkY + textHeight],
      Border: [0, 0, 0],
      C: [0, 0, 1],
      A: {
        Type: 'Action',
        S: 'URI',
        URI: PDFString.of(driveLink),
      },
    });

    const linkAnnotationRef = pdfDoc.context.register(linkAnnotation);
    const annotations = firstPage.node.Annots();
    if (annotations) {
      annotations.push(linkAnnotationRef);
    } else {
      firstPage.node.set(PDFName.of('Annots'), pdfDoc.context.obj([linkAnnotationRef]));
    }

    pdfDoc.setTitle(`${productTitle} - Digital Download`);
    pdfDoc.setAuthor('Your Business Name');
    pdfDoc.setSubject('Digital Art Download Instructions');
    pdfDoc.setCreationDate(new Date());

    const pdfBytes = await pdfDoc.save();
    const pdfBlob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });

    const timestamp = Date.now();
    const slug = generateSlug(productTitle).replace(/-\d+$/, '');
    const fileName = `pdfs/${slug}-${timestamp}.pdf`;
    
    const pdfUrl = await uploadToBunny(pdfBlob, fileName, 'application/pdf', credentials);

    return { pdfBlob, pdfUrl };
  }

  async fetchReadmePdf(
    productCategory: ProductCategory,
    getSettings: () => Promise<{ categoryReadmePdfs?: Record<string, string> }>
  ): Promise<Blob | null> {
    logger.debug('PdfService', 'Fetching settings...');
    const settings = await getSettings();
    
    const readmePdfUrl = settings.categoryReadmePdfs?.[productCategory];
    logger.debug('PdfService', `README URL for ${productCategory}:`, readmePdfUrl);

    if (!readmePdfUrl) {
      logger.debug('PdfService', `No README PDF configured for category: ${productCategory}`);
      return null;
    }

    const proxyUrl = `${window.location.origin}/api/proxy/fetch-pdf?url=${encodeURIComponent(readmePdfUrl)}`;
    logger.debug('PdfService', `Fetching via proxy: ${proxyUrl}`);

    const response = await fetch(proxyUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch README PDF: ${response.status} ${response.statusText}`);
    }

    const pdfBlob = await response.blob();
    logger.debug('PdfService', `PDF fetched, size: ${pdfBlob.size} bytes`);

    return pdfBlob;
  }
}
